import { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import type { CycleRing } from '@/lib/cycleTypes';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getLeapCyclePosition } from '@/lib/cycleConfig';

const SYNODIC_MONTH = 30.1375;
const KNOWN_NEW_MOON = new Date('2012-12-13T08:42:00Z').getTime();
const SWIPE_DEADZONE_PX = 8;
const RING_BUTTON_LONG_PRESS_MS = 550;

export type TimeScale = 'day' | 'week' | 'moon' | 'year' | 'full';

interface CycleGraphProps {
	rings: CycleRing[];
	allRings?: CycleRing[];
	currentDayOfYear: number;
	year?: number;
	onDayChange?: (day: number, fractionOfDay: number) => void;
	onInteractionStart?: () => void;
	onToggleRing?: (ringId: string) => void;
	className?: string;
	scaleOverride?: TimeScale | null;
	onScaleChange?: (scale: TimeScale) => void;
}

interface DragAnchor {
	clientX: number;
	dayAtStart: number;
	spanDays: number;
	chartWidth: number;
}

interface ScaleConfig {
	label: string;
	range: number;
	centerLabel: string;
	samplesPerDay: number;
}

const SCALE_ORDER: TimeScale[] = ['day', 'week', 'moon', 'year', 'full'];

const SCALE_CONFIG: Record<TimeScale, ScaleConfig> = {
	day: { label: '1 Day', range: 1, centerLabel: 'Now', samplesPerDay: 96 },
	week: { label: '1 Week', range: 7, centerLabel: 'Now', samplesPerDay: 24 },
	moon: { label: '28 Days', range: 28, centerLabel: 'Now', samplesPerDay: 6 },
	year: { label: '1 Year', range: 365, centerLabel: 'Now', samplesPerDay: 1 },
	full: { label: 'Full', range: 365, centerLabel: 'Full', samplesPerDay: 1 },
};

const normalizePhase = (value: number) => ((value % 1) + 1) % 1;

const wrapDayOfYear = (dayOfYearFloat: number, maxDay = 365) => {
	const wrapped = ((dayOfYearFloat - 1) % maxDay + maxDay) % maxDay + 1;
	return wrapped;
};

const getWaveformValue = (phase: number, waveform: CycleRing['waveform']): number => {
	const normalized = normalizePhase(phase);
	switch (waveform) {
		case 'square':
			return normalized < 0.5 ? 1 : 0;
		case 'triangle':
			return 1 - Math.abs(normalized * 2 - 1);
		case 'sawtooth':
		case 'default':
			return normalized;
		case '24hr':
		case 'sine':
		default:
			return (Math.sin(normalized * 2 * Math.PI - Math.PI / 2) + 1) / 2;
	}
};

const getDateForDayOfYear = (dayOfYearFloat: number, year: number) => {
	const wrappedDay = wrapDayOfYear(dayOfYearFloat);
	const wholeDay = Math.floor(wrappedDay);
	const fractionOfDay = wrappedDay - wholeDay;
	const base = new Date(Date.UTC(year, 0, 1));
	base.setUTCDate(wholeDay);
	const totalSeconds = fractionOfDay * 86400;
	base.setUTCHours(0, 0, 0, 0);
	base.setUTCSeconds(totalSeconds);
	return { date: base, fractionOfDay };
};

const getMoonPhaseForDate = (date: Date) => {
	const daysSinceNewMoon = (date.getTime() - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
	let phase = (daysSinceNewMoon % SYNODIC_MONTH) / SYNODIC_MONTH;
	if (phase < 0) phase += 1;
	return phase;
};

const ringColorFallbacks: Record<string, string> = {
	lunar: 'hsl(220, 60%, 70%)',
	moons: 'hsl(280, 60%, 60%)',
	days: 'hsl(45, 80%, 60%)',
	seals: 'hsl(0, 70%, 60%)',
	colors: 'hsl(160, 60%, 50%)',
	plasmas: 'hsl(320, 70%, 60%)',
};

const getStableRingFallback = (ringId: string) => {
	const mapped = ringColorFallbacks[ringId];
	if (mapped) return mapped;

	const seed = ringId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
	const hue = (seed * 53) % 360;
	return `hsl(${hue},60%,60%)`;
};

const getRingStroke = (ring: CycleRing, _index: number) => {
	const explicit = ring.colors?.[ring.currentStage ?? 0] ?? ring.colors?.[0];
	if (explicit) return `hsl(${explicit})`;
	return getStableRingFallback(ring.id);
};

const parseHslComponents = (value: string) => {
	const cleaned = value.trim().replace(/^hsl\(/i, '').replace(/\)$/i, '').split('/')[0].trim();
	const match = cleaned.match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%?\s+(-?\d+(?:\.\d+)?)%?$/);
	if (!match) return null;
	return {
		h: Number(match[1]),
		s: Number(match[2]),
		l: Number(match[3]),
	};
};

const interpolateStageColor = (current: string, next: string, t: number) => {
	const a = parseHslComponents(current);
	const b = parseHslComponents(next);
	if (!a || !b) return `hsl(${current})`;
	const ratio = Math.max(0, Math.min(1, t));
	const hueDelta = ((((b.h - a.h) % 360) + 540) % 360) - 180;
	const h = (a.h + hueDelta * ratio + 360) % 360;
	const s = a.s + (b.s - a.s) * ratio;
	const l = a.l + (b.l - a.l) * ratio;
	return `hsl(${h.toFixed(2)} ${s.toFixed(2)}% ${l.toFixed(2)}%)`;
};

const getLineType = (waveform: CycleRing['waveform']) => {
	switch (waveform) {
		case 'square':
			return 'stepAfter' as const;
		case 'triangle':
		case 'sawtooth':
		case 'default':
			return 'linear' as const;
		case '24hr':
		case 'sine':
		default:
			return 'monotone' as const;
	}
};

const formatDayLabel = (day: number) => {
	const wrappedDay = wrapDayOfYear(day);
	const whole = Math.trunc(wrappedDay);
	const fraction = Math.round((wrappedDay - whole) * 24);
	if (fraction === 0) return `Day ${whole}`;
	return `Day ${whole} · ${fraction}h`;
};

const formatExpandedDayLabel = (day: number) => {
	const wrappedDay = wrapDayOfYear(day);
	const whole = Math.trunc(wrappedDay);
	const fraction = (wrappedDay - whole) * 24;
	const hours = Math.floor(fraction);
	const minutes = Math.round((fraction - hours) * 60);
	if (hours === 0 && minutes === 0) return `Day ${whole}`;
	if (minutes === 0) return `Day ${whole} · ${hours}h`;
	return `Day ${whole} · ${hours}h ${minutes}m`;
};

export function CycleGraph({ rings, allRings, currentDayOfYear, year = 2025, onDayChange, onInteractionStart, onToggleRing, className, scaleOverride, onScaleChange }: CycleGraphProps) {
	const gradientPrefix = useId().replace(/[:]/g, '');
	const initialScale = useMemo<TimeScale>(() => {
		if (rings.some((ring) => ring.id === 'days')) return 'day';
		if (rings.some((ring) => ring.id === 'lunar' || ring.id === 'moons' || (ring.name ?? '').toLowerCase().includes('moon'))) {
			return 'moon';
		}
		const minStages = rings.length ? Math.min(...rings.map((ring) => ring.stages || 0)) : null;
		if (minStages !== null && minStages > 0 && minStages <= 30) return 'moon';
		return 'full';
	}, [rings]);

	const [scaleInternal, setScaleInternal] = useState<TimeScale>(initialScale);
	const effectiveScale = scaleOverride ?? scaleInternal;
	const userSelectedScale = useRef(false);
	const maxDay = useMemo(() => {
		if (effectiveScale === 'full') {
			return Math.max(365, ...rings.map((ring) => Math.max(1, ring.stages ?? 365)));
		}
		return 365;
	}, [rings, effectiveScale]);

	const clampDay = useCallback(
		(value: number) => Math.min(maxDay, Math.max(1, value)),
		[maxDay],
	);

	const [indicatorDay, setIndicatorDay] = useState(() => clampDay(currentDayOfYear));
	const [isDragging, setIsDragging] = useState(false);
	const [previewDay, setPreviewDay] = useState<number | null>(null);
	const isDraggingRef = useRef(false);
	const dragAnchorRef = useRef<DragAnchor | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);
	const [yAxisWidth, setYAxisWidth] = useState(70); // default fallback
	const [hoverGuideLeft, setHoverGuideLeft] = useState<string | null>(null);
	const ringLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const ringLongPressTriggeredRef = useRef<string | null>(null);

	// Track container width so indicator line can align with the chart area (accounts for YAxis + margins)
	useLayoutEffect(() => {
		const element = containerRef.current;
		if (!element) return;

		const updateSize = () => {
			setContainerWidth(element.getBoundingClientRect().width);
			// Measure rendered YAxis width from the SVG to avoid hardcoded 70px
			const yAxis = element.querySelector<SVGGElement>('g.recharts-cartesian-axis.yAxis');
			if (yAxis) {
				const box = yAxis.getBBox();
				if (box && box.width > 0) {
					setYAxisWidth(box.width);
				}
			}
		};
		updateSize();

		const observer = new ResizeObserver(updateSize);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (scaleOverride) return;
		if (!userSelectedScale.current) {
			if (rings.some((ring) => ring.id === 'days')) {
				setScaleInternal('day');
			} else if (rings.some((ring) => ring.id === 'lunar' || ring.id === 'moons' || (ring.name ?? '').toLowerCase().includes('moon'))) {
				setScaleInternal('moon');
			} else {
				setScaleInternal('full');
			}
		}
	}, [rings, scaleOverride]);

	// Update indicator day when currentDayOfYear changes (e.g., new time selected elsewhere)
	useEffect(() => {
		const newIndicator = clampDay(currentDayOfYear);
		setIndicatorDay(newIndicator);
	}, [currentDayOfYear, clampDay]);

	const scaleConfig = SCALE_CONFIG[effectiveScale];

	const cancelRingButtonLongPress = useCallback(() => {
		if (ringLongPressTimerRef.current) {
			clearTimeout(ringLongPressTimerRef.current);
			ringLongPressTimerRef.current = null;
		}
	}, []);

	const soloRingVisibility = useCallback(
		(ringId: string) => {
			if (!onToggleRing || !allRings || allRings.length === 0) return;
			const visibleIds = new Set(rings.map((ring) => ring.id));

			allRings.forEach((ring) => {
				if (ring.id !== ringId && visibleIds.has(ring.id)) {
					onToggleRing(ring.id);
				}
			});

			if (!visibleIds.has(ringId)) {
				onToggleRing(ringId);
			}
		},
		[allRings, onToggleRing, rings],
	);

	const startRingButtonLongPress = useCallback(
		(ringId: string) => {
			cancelRingButtonLongPress();
			ringLongPressTriggeredRef.current = null;
			ringLongPressTimerRef.current = setTimeout(() => {
				ringLongPressTriggeredRef.current = ringId;
				soloRingVisibility(ringId);
				ringLongPressTimerRef.current = null;
			}, RING_BUTTON_LONG_PRESS_MS);
		},
		[cancelRingButtonLongPress, soloRingVisibility],
	);

	useEffect(() => () => cancelRingButtonLongPress(), [cancelRingButtonLongPress]);

	// Waveform scrolls to keep indicator centered, not the indicator moving
	const visibleRange = useMemo(() => {
		const half = scaleConfig.range / 2;
		const start = indicatorDay - half;
		const end = indicatorDay + half;
		return { start, end };
	}, [scaleConfig.range, indicatorDay]);

	// Sync indicatorDay with currentDayOfYear when not dragging
	useEffect(() => {
		if (!isDraggingRef.current) {
			setIndicatorDay(clampDay(currentDayOfYear));
		}
	}, [currentDayOfYear, clampDay]);

	const span = visibleRange.end - visibleRange.start;

	const applyScale = (next: TimeScale) => {
		userSelectedScale.current = true;
		if (!scaleOverride) {
			setScaleInternal(next);
		}
		onScaleChange?.(next);
	};

	const ringMap = useMemo(() => {
		const source = allRings && allRings.length > 0 ? allRings : rings;
		return new Map(source.map((ring) => [ring.id, ring]));
	}, [allRings, rings]);

	const resolveExactPosition = useCallback(
		(ring: CycleRing, dayOfYearFloat: number) => {
			const cache = new Map<string, number>();
			const resolve = (target: CycleRing): number => {
				if (cache.has(target.id)) {
					return cache.get(target.id)!;
				}

				const stages = Math.max(1, target.stages ?? 1);
				const wrappedDay = wrapDayOfYear(dayOfYearFloat);
				const wholeDay = Math.floor(wrappedDay);
				const fractionOfDay = wrappedDay - wholeDay;
				const isDrift = wholeDay === 365;
				const dayInMonth = isDrift ? 0 : ((wholeDay - 1) % 28) + 1;
				const monthNum = isDrift ? 13 : Math.floor((wholeDay - 1) / 28) + 1;

				let exactPosition: number;

				if (target.id === 'days') {
					exactPosition = isDrift ? 27.5 + fractionOfDay : (dayInMonth - 1 + fractionOfDay);
			} else if (target.id === 'moons' || (target.stages === 13 && (target.name ?? '').toLowerCase().includes('moon'))) {
					if (isDrift) {
						exactPosition = 12.5 + fractionOfDay;
					} else {
						const monthProgress = (dayInMonth - 1 + fractionOfDay) / 28;
						exactPosition = (monthNum - 1 + monthProgress);
					}
				} else if (target.id === 'gregorian-leap') {
					const { date, fractionOfDay: frac } = getDateForDayOfYear(dayOfYearFloat, year);
					const { stageIndex, progress } = getLeapCyclePosition(date, frac);
					exactPosition = stageIndex + progress;
				} else if (target.id === 'lunar') {
					const { date } = getDateForDayOfYear(dayOfYearFloat, year);
					const phase = getMoonPhaseForDate(date);
					exactPosition = phase * stages;
				} else if (target.parentId && target.stagesPerParent) {
					if (target.parentId === '__timeline__') {
						exactPosition = (dayOfYearFloat - 1) * target.stagesPerParent;
					} else {
						const parentRing = ringMap.get(target.parentId);
						if (parentRing) {
							const parentPosition = resolve(parentRing);
							exactPosition = parentPosition * target.stagesPerParent;
						} else {
							const fallback = (dayOfYearFloat - 1) % stages;
							exactPosition = fallback < 0 ? fallback + stages : fallback;
						}
					}
				} else {
					const base = (dayOfYearFloat - 1) % stages;
					exactPosition = base < 0 ? base + stages : base;
				}

				cache.set(target.id, exactPosition);
				return exactPosition;
			};

			return resolve(ring);
		},
		[ringMap, year],
	);

	const getCycleValueForDay = useCallback(
		(ring: CycleRing, dayOfYearFloat: number) => {
			const stages = Math.max(1, ring.stages ?? 1);
			if (ring.useCurrentStageAsValue && ring.currentStage !== undefined) {
				const safeDenominator = stages > 1 ? stages - 1 : 1;
				const rawValue = ring.currentStage / safeDenominator;
				return Math.min(1, Math.max(0, rawValue));
			}
			const exactPosition = resolveExactPosition(ring, dayOfYearFloat);
			const basePhase = normalizePhase(exactPosition / stages);
			const multiplier = ring.waveformIntervalMultiplier ?? 1;
			const divider = ring.waveformIntervalDivider ?? 1;
			const safeDivider = divider === 0 ? 1 : divider;
			const phase = (basePhase * multiplier) / safeDivider;
			return getWaveformValue(phase, ring.waveform ?? 'default');
		},
		[resolveExactPosition],
	);

	const getStageColorForDay = useCallback(
		(ring: CycleRing, dayOfYearFloat: number, index: number) => {
			const palette = (ring.colors ?? []).filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
			if (!palette.length) return getRingStroke(ring, index);
			const stages = Math.max(1, ring.stages ?? palette.length);
			const exactPosition = resolveExactPosition(ring, dayOfYearFloat);
			const baseIndex = Math.floor(exactPosition);
			const stageIndex = ((baseIndex % stages) + stages) % stages;
			const nextStageIndex = (stageIndex + 1) % stages;
			const blend = exactPosition - baseIndex;
			const currentColor = palette[stageIndex % palette.length];
			const nextColor = palette[nextStageIndex % palette.length];
			return interpolateStageColor(currentColor, nextColor, blend);
		},
		[resolveExactPosition],
	);

	const data = useMemo(() => {
		// Filter to ensure only valid rings with IDs are processed
		const validRings = rings.filter((ring) => ring && ring.id);
		if (!validRings.length) return [];

		if (effectiveScale === 'year') {
			return Array.from({ length: 365 }, (_, index) => {
				const day = index + 1;
				const row: Record<string, number> = { day };
				validRings.forEach((ring) => {
					row[ring.id] = getCycleValueForDay(ring, day);
				});
				return row;
			});
		}

		const samplesPerDay = effectiveScale === 'full' ? 1 : scaleConfig.samplesPerDay;
		const safeSpan = Number.isFinite(span) && span > 0 ? span : 1;
		const totalSamples = Math.max(2, Math.min(600, Math.ceil(safeSpan * samplesPerDay)));
		const step = safeSpan / Math.max(1, totalSamples - 1);
		const start = Number.isFinite(visibleRange.start) ? visibleRange.start : clampDay(currentDayOfYear);
		return Array.from({ length: totalSamples }, (_, index) => {
			const day = start + index * step;
			const row: Record<string, number> = { day };
			validRings.forEach((ring) => {
				row[ring.id] = getCycleValueForDay(ring, day);
			});
			return row;
		});
	}, [rings, effectiveScale, scaleConfig.samplesPerDay, span, visibleRange.start, clampDay, indicatorDay, currentDayOfYear]); // eslint-disable-line react-hooks/exhaustive-deps

	const getDayFromClientX = useCallback(
		(clientX: number) => {
			const element = containerRef.current;
			if (!element) return clampDay(indicatorDay);

			const rect = element.getBoundingClientRect();
			const chartMarginLeft = yAxisWidth + 16; // measured YAxis width + chart left margin
			const chartMarginRight = 16;
			const chartWidth = Math.max(1, rect.width - chartMarginLeft - chartMarginRight);
			const offsetX = clientX - rect.left - chartMarginLeft;
			const ratio = Math.max(0, Math.min(1, offsetX / chartWidth));
			const day = visibleRange.start + ratio * (visibleRange.end - visibleRange.start);
			const result = wrapDayOfYear(day, maxDay);
			const guideLeftPx = chartMarginLeft + ratio * chartWidth;
			setHoverGuideLeft(`${(guideLeftPx / rect.width) * 100}%`);
			return result;
		},
		[indicatorDay, maxDay, visibleRange, yAxisWidth],
	);

	const updatePreview = useCallback(
		(dayFloat: number) => {
			const clamped = wrapDayOfYear(dayFloat, maxDay);
			setPreviewDay(clamped);
			setIndicatorDay(clamped);
		},
		[maxDay],
	);

	const handlePointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!onDayChange) return;
			onInteractionStart?.();
			event.preventDefault();
			isDraggingRef.current = true;
			setIsDragging(true);
			event.currentTarget.setPointerCapture(event.pointerId);
			const element = containerRef.current;
			if (!element) return;
			const rect = element.getBoundingClientRect();
			const chartMarginLeft = yAxisWidth + 16;
			const chartMarginRight = 16;
			const chartWidth = Math.max(1, rect.width - chartMarginLeft - chartMarginRight);
			dragAnchorRef.current = {
				clientX: event.clientX,
				dayAtStart: clampDay(indicatorDay),
				spanDays: Math.max(0.01, visibleRange.end - visibleRange.start),
				chartWidth,
			};
			updatePreview(clampDay(indicatorDay));
		},
		[clampDay, indicatorDay, onDayChange, onInteractionStart, updatePreview, visibleRange.end, visibleRange.start, yAxisWidth],
	);

	const handlePointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (isDraggingRef.current && onDayChange) {
				const anchor = dragAnchorRef.current;
				const day = anchor
					? wrapDayOfYear(
						Math.abs(event.clientX - anchor.clientX) < SWIPE_DEADZONE_PX
							? anchor.dayAtStart
							: anchor.dayAtStart + ((event.clientX - anchor.clientX) / anchor.chartWidth) * anchor.spanDays,
					)
					: getDayFromClientX(event.clientX);
				updatePreview(day);
			} else if (!isDraggingRef.current) {
				const day = getDayFromClientX(event.clientX);
				// Hovering: just update preview (display day in upper right)
				setPreviewDay(day);
			}
		},
		[getDayFromClientX, onDayChange, updatePreview],
	);

	const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDraggingRef.current) return;
		if (previewDay !== null && onDayChange) {
			const whole = Math.floor(previewDay);
			const fraction = previewDay - whole;
			onDayChange(whole, fraction);
		}
		isDraggingRef.current = false;
		dragAnchorRef.current = null;
		setIsDragging(false);
		event.currentTarget.releasePointerCapture(event.pointerId);
		setPreviewDay(null);
		setHoverGuideLeft(null);
	}, [onDayChange, previewDay]);

	const handleTouchStart = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			if (!onDayChange || event.touches.length !== 1) return;
			event.preventDefault();
			const touch = event.touches[0];
			isDraggingRef.current = true;
			setIsDragging(true);
			const element = containerRef.current;
			if (!element) return;
			const rect = element.getBoundingClientRect();
			const chartMarginLeft = yAxisWidth + 16;
			const chartMarginRight = 16;
			const chartWidth = Math.max(1, rect.width - chartMarginLeft - chartMarginRight);
			dragAnchorRef.current = {
				clientX: touch.clientX,
				dayAtStart: clampDay(indicatorDay),
				spanDays: Math.max(0.01, visibleRange.end - visibleRange.start),
				chartWidth,
			};
			updatePreview(clampDay(indicatorDay));
		},
		[clampDay, indicatorDay, onDayChange, updatePreview, visibleRange.end, visibleRange.start, yAxisWidth],
	);

	const handleTouchMove = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			if (!onDayChange || !isDraggingRef.current || event.touches.length !== 1) return;
			const anchor = dragAnchorRef.current;
			const day = anchor
				? wrapDayOfYear(
					Math.abs(event.touches[0].clientX - anchor.clientX) < SWIPE_DEADZONE_PX
						? anchor.dayAtStart
						: anchor.dayAtStart + ((event.touches[0].clientX - anchor.clientX) / anchor.chartWidth) * anchor.spanDays,
				)
				: getDayFromClientX(event.touches[0].clientX);
			updatePreview(day);
		},
		[getDayFromClientX, onDayChange, updatePreview],
	);

	const handleTouchEnd = useCallback(() => {
		if (previewDay !== null && onDayChange) {
			const whole = Math.floor(previewDay);
			const fraction = previewDay - whole;
			onDayChange(whole, fraction);
		}
		isDraggingRef.current = false;
		dragAnchorRef.current = null;
		setIsDragging(false);
		setPreviewDay(null);
		setHoverGuideLeft(null);
	}, [onDayChange, previewDay]);

	const handlePointerLeave = useCallback(() => {
		if (!isDraggingRef.current) {
			setPreviewDay(null);
		}
		setHoverGuideLeft(null);
	}, []);

	// Static indicator aligned to the chart's center (accounts for YAxis width + chart margins)
	const indicatorLeft = useMemo(() => {
		if (!containerWidth) return '50%';
		const chartMarginLeft = yAxisWidth + 16; // measured YAxis width + chart left margin
		const chartMarginRight = 16;
		const chartWidth = Math.max(1, containerWidth - chartMarginLeft - chartMarginRight);
		const px = chartMarginLeft + chartWidth / 2;
		return `${(px / containerWidth) * 100}%`;
	}, [containerWidth, yAxisWidth]);

	// Display day (preview while dragging, actual otherwise)
	const displayDay = previewDay !== null ? previewDay : indicatorDay;

	const ticks = useMemo(() => {
		if (effectiveScale === 'day') {
			return Array.from({ length: 5 }, (_, index) => visibleRange.start + (span * index) / 4);
		}
		if (effectiveScale === 'week' || effectiveScale === 'moon') {
			const start = Math.ceil(visibleRange.start);
			const end = Math.floor(visibleRange.end);
			const out: number[] = [];
			for (let day = start; day <= end; day += 1) out.push(day);
			return out;
		}
		if (effectiveScale === 'year') {
			return Array.from({ length: 13 }, (_, index) => 1 + index * 28);
		}
		const approxStep = Math.max(1, Math.floor(span / 8));
		const start = Math.ceil(visibleRange.start);
		const end = Math.floor(visibleRange.end);
		const out: number[] = [];
		for (let day = start; day <= end; day += approxStep) out.push(day);
		if (!out.includes(end)) out.push(end);
		return out;
	}, [effectiveScale, span, visibleRange.start, visibleRange.end]);

	const summaryLabel = useMemo(() => formatDayLabel(displayDay), [displayDay]);
	const expandedSummaryLabel = useMemo(() => formatExpandedDayLabel(displayDay), [displayDay]);

	const legendItems = useMemo(
		() => {
			const validRings = rings.filter((ring) => ring && ring.id);
			return validRings.map((ring, index) => {
				const value = getCycleValueForDay(ring, displayDay);
				const percent = Math.round(value * 100);
				const degrees = Math.round(value * 360);
				return {
				id: ring.id,
				name: ring.name ?? 'Unnamed',
				color: getRingStroke(ring, index),
				value: percent,
				degrees: degrees,
			};
		});
	}, [rings, displayDay, getCycleValueForDay]);

	return (
		<div
			className={cn(
				'group relative flex h-full flex-col',
				className,
			)}
		>
			<div className="flex flex-col gap-2 pointer-events-auto">
				<div className="flex items-start justify-between gap-2 bg-transparent rounded-lg px-3 py-2">
					<div className="flex min-w-0 flex-1 gap-0.5 overflow-x-auto whitespace-nowrap pr-1 sm:gap-1">
						{SCALE_ORDER.map((key) => {
							const cfg = SCALE_CONFIG[key];
							return (
								<Button
									key={key}
									size="sm"
									variant={effectiveScale === key ? 'default' : 'outline'}
									onClick={() => applyScale(key)}
									className="h-7 shrink-0 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs"
								>
									{cfg.label}
								</Button>
							);
						})}
					</div>
					<div className="hidden text-[10px] sm:block sm:text-xs text-muted-foreground whitespace-nowrap">{summaryLabel}</div>
				</div>
				{allRings && allRings.length > 0 && onToggleRing && (
					<div className="flex flex-wrap gap-1.5">
						{allRings.map((ring, index) => {
							const isVisible = rings.some(r => r.id === ring.id);
							const color = getRingStroke(ring, index);
							return (
								<button
									key={ring.id}
									onClick={() => {
										if (ringLongPressTriggeredRef.current === ring.id) {
											ringLongPressTriggeredRef.current = null;
											return;
										}
										cancelRingButtonLongPress();
										onToggleRing(ring.id);
									}}
									onPointerDown={(event) => {
										if (event.pointerType === 'mouse' && event.button !== 0) return;
										startRingButtonLongPress(ring.id);
									}}
									onPointerUp={cancelRingButtonLongPress}
									onPointerLeave={cancelRingButtonLongPress}
									onPointerCancel={cancelRingButtonLongPress}
									onTouchStart={() => startRingButtonLongPress(ring.id)}
									onTouchEnd={cancelRingButtonLongPress}
									onTouchCancel={cancelRingButtonLongPress}
									className={cn(
										"flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] sm:gap-1.5 sm:px-2 sm:py-1 sm:text-xs transition-all",
										isVisible
											? "border-border bg-background/80 opacity-100"
											: "border-border/40 bg-background/40 opacity-50"
									)}
										title={`${isVisible ? `Hide ${ring.name ?? 'Unnamed'}` : `Show ${ring.name ?? 'Unnamed'}`} (long press to solo)`}
								>
									<div
										className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm flex-shrink-0"
										style={{ backgroundColor: color }}
									/>
									<span className={cn("truncate max-w-[60px] sm:max-w-none", isVisible ? "font-medium" : "")}>
										{ring.name ?? 'Unnamed'}
									</span>
								</button>
							);
						})}
					</div>
				)}
			</div>

			<div
				ref={containerRef}
				className={cn(
					'relative flex-1',
					onDayChange ? 'cursor-crosshair touch-none select-none' : 'pointer-events-none',
				)}
				tabIndex={onDayChange ? 0 : -1}
				onFocus={onInteractionStart}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={endDrag}
				onPointerCancel={endDrag}
				onPointerLeave={handlePointerLeave}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				{rings.length === 0 ? (
					<div className="absolute inset-0 flex items-center justify-center px-4 text-sm text-destructive bg-background/80">
						No visible cycles. Add or unhide a cycle to see waveforms.
					</div>
				) : data.length <= 1 ? (
					<div className="absolute inset-0 flex items-center justify-center px-4 text-sm text-muted-foreground">
						Not enough data to render waveforms. Try widening the range or adding more cycles.
					</div>
				) : (
					<>
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={data} margin={{ top: 12, right: 16, bottom: 35, left: 16 }}>
								<defs>
									{rings.map((ring, index) => {
										const gradientId = `${gradientPrefix}-${ring.id}-line-gradient`;
										const sampleCount = Math.max(2, Math.min(80, Math.ceil(span * 6)));
										const gradientColors = Array.from({ length: sampleCount }, (_, sampleIndex) => {
											const t = sampleCount === 1 ? 0 : sampleIndex / (sampleCount - 1);
											const day = visibleRange.start + t * span;
											return getStageColorForDay(ring, day, index);
										});
										return (
											<linearGradient key={gradientId} id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
												{gradientColors.map((color, colorIndex) => {
													const offset = gradientColors.length === 1
														? 0
														: (colorIndex / (gradientColors.length - 1)) * 100;
													return <stop key={`${gradientId}-stop-${colorIndex}`} offset={`${offset}%`} stopColor={color} />;
												})}
											</linearGradient>
										);
									})}
								</defs>
								<CartesianGrid stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
								<XAxis
									dataKey="day"
									type="number"
									domain={[visibleRange.start, visibleRange.end]}
									tickFormatter={(value) => {
										const dayNum = Math.round(value);
										const wrappedDay = Math.round(wrapDayOfYear(value));
										const degrees = ((wrappedDay - 1) * (360 / 365)) % 360;
										return `${wrappedDay} (${Math.round(degrees)}°)`;
									}}
									ticks={ticks}
									stroke="rgba(255,255,255,0.5)"
									tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.7)' }}
									label={{ value: 'Day (Degrees)', position: 'insideBottom', offset: -5, fontSize: 11 }}
								/>
								<YAxis
									domain={[0, 1]}
									tickFormatter={(value) => {
										const percent = Math.round((value as number) * 100);
										const degrees = Math.round((value as number) * 360);
										return `${percent}% (${degrees}°)`;
									}}
									stroke="rgba(255,255,255,0.5)"
									tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.7)' }}
									width={70}
									label={{ value: 'Phase % (Degrees)', angle: -90, position: 'insideLeft', fontSize: 11 }}
								/>
								<RechartsTooltip
									formatter={(value: unknown) => `${Math.round((value as number) * 100)}%`}
									labelFormatter={(value: unknown) => formatDayLabel(Number(value))}
									position={{ x: 12, y: 12 }}
									wrapperStyle={{ pointerEvents: 'none', zIndex: 100 }}
									contentStyle={{
										background: 'var(--background)',
										border: '1px solid var(--border)',
										borderRadius: 6,
										boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
										padding: '4px 6px',
										fontSize: 11,
									}}
									allowEscapeViewBox={{ x: true, y: true }}
									isAnimationActive={false}
								/>
								{rings.map((ring, index) => (
									<Line
										type={getLineType(ring.waveform)}
										key={ring.id}
										dataKey={ring.id}
										stroke={`url(#${gradientPrefix}-${ring.id}-line-gradient)`}
										strokeWidth={3.2}
										dot={false}
										strokeLinejoin="miter"
										strokeLinecap="square"
										isAnimationActive={false}
										style={{ filter: 'drop-shadow(0 0 5px currentColor)' }}
									/>
								))}
							</LineChart>
						</ResponsiveContainer>
						{hoverGuideLeft && (
							<div
								className="pointer-events-none absolute inset-y-3 w-px bg-white/35"
								style={{ left: hoverGuideLeft }}
							/>
						)}
						{indicatorLeft && (
							<div
								className="pointer-events-none absolute inset-y-3 w-[2px] bg-fuchsia-300 shadow-[0_0_10px_rgba(244,114,182,0.7)]"
								style={{ left: indicatorLeft }}
							/>
						)}
					</>
				)}
				{/* Expanded date display during drag only */}
				{isDragging && (
					<div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-md border border-primary/50 bg-background/95 px-3 py-1.5 text-sm font-semibold shadow-lg backdrop-blur-md">
						{expandedSummaryLabel}
					</div>
				)}
			</div>


		</div>
	);
}
